import { useMemo, useState, useEffect } from 'react';
import { ScrollableModal } from '../../components/ui/ScrollableModal';
import DropdownSearch from '../../components/ui/DropdownSearch';
import FormField from '../../components/forms/FormField';
import { Users, Loader2 } from 'lucide-react';
import { organisationsAPI, streamsAPI } from '../../services/api';
import toast from '../../services/toast';

export default function AssignStreamMemberModal({
  isOpen,
  onClose,
  orgId,
  streamId,
  streamName,
  onSuccess,
}: any) {
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [streamMemberIds, setStreamMemberIds] = useState(new Set());
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!isOpen || !orgId || !streamId) return;
    setSelectedUserId('');
    setFetching(true);
    Promise.all([organisationsAPI.getMembers(orgId), streamsAPI.getMembers(orgId, streamId)])
      .then(([members, assigned]) => {
        setOrgMembers(Array.isArray(members) ? members : []);
        setStreamMemberIds(new Set((assigned || []).map((m) => m.userId || m.id)));
      })
      .catch(() => {
        setOrgMembers([]);
        setStreamMemberIds(new Set());
        toast.error('Failed to load members');
      })
      .finally(() => setFetching(false));
  }, [isOpen, orgId, streamId]);

  const unassigned = useMemo(
    () => orgMembers.filter((m) => m.id && !streamMemberIds.has(m.id)),
    [orgMembers, streamMemberIds]
  );

  const memberOptions = useMemo(
    () =>
      unassigned.map((m) => ({
        id: m.id,
        name: m.username || m.email || m.id,
        email: m.email || '',
      })),
    [unassigned]
  );

  const selectedOption = memberOptions.find((o) => o.id === selectedUserId) ?? null;

  useEffect(() => {
    if (selectedUserId && !memberOptions.some((o) => o.id === selectedUserId)) {
      setSelectedUserId('');
    }
  }, [selectedUserId, memberOptions]);

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      await streamsAPI.addMember(orgId, streamId, selectedUserId);
      setStreamMemberIds((prev) => new Set([...prev, selectedUserId]));
      setSelectedUserId('');
      if (onSuccess) onSuccess();
      toast.success('Member added to stream');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <ScrollableModal
      isOpen={isOpen}
      onClose={handleClose}
      title={streamName ? `Add member to ${streamName}` : 'Add member to stream'}
      icon={<Users className="w-5 h-5 text-vivid-green-cyan" />}
      disableClose={loading}
      footer={
        <button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className="btn-secondary-mdl disabled:opacity-50"
        >
          Done
        </button>
      }
    >
      <div>
        {fetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-vivid-red" />
          </div>
        ) : unassigned.length === 0 ? (
          <p className="text-slate-400">
            All organisation members are already assigned to this stream.
          </p>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <FormField id="stream-assign-member" label="Organisation member" className="min-w-0 flex-1">
              <DropdownSearch
                id="stream-assign-member"
                options={memberOptions}
                value={selectedOption}
                onChange={(opt) => setSelectedUserId(opt?.id ?? '')}
                getOptionValue={(o) => o.id}
                getOptionLabel={(o) => o.name}
                getOptionSublabel={(o) => o.email || undefined}
                placeholder="Search members…"
                emptyMessage="No matching members"
                disabled={loading}
                usePortal
              />
            </FormField>
            <button
              type="button"
              onClick={handleAdd}
              disabled={loading || !selectedUserId}
              className="btn-primary-mdl shrink-0 disabled:opacity-50"
            >
              {loading ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}
      </div>
    </ScrollableModal>
  );
}
